import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import { questionsRead } from '../components/Questions/useUnread';
import Conversation from '../components/Questions/Conversation';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });
const dayOf = (v) => (v ? new Date(v).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : '');

/* /Admin/questions: every student <-> teacher conversation, reported first. Hide a message, mark a report as checked. */
const TeacherQuestions = () => {
  const [params, setParams] = useSearchParams();
  const sid = params.get('student');
  const tid = params.get('teacher');
  const [threads, setThreads] = useState(null);
  const [messages, setMessages] = useState(null);
  const [error, setError] = useState('');

  const loadThreads = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/questions`, auth());
      setThreads(res.data.threads || []);
      questionsRead(); // the menu badge updates
    } catch (err) {
      setError(err.response?.data?.error || 'We couldn’t load the conversations.');
      setThreads([]);
    }
  }, []);
  const loadMessages = useCallback(async () => {
    if (!sid || !tid) return setMessages(null);
    try {
      const res = await axios.get(`${API}/api/admin/questions/${sid}/${tid}`, auth());
      setMessages(res.data.messages || []);
    } catch {
      setMessages([]);
    }
    return undefined;
  }, [sid, tid]);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const current = threads?.find((t) => t.studentId === sid && t.teacherId === tid);
  const update = async (m, body) => {
    await axios.put(`${API}/api/admin/questions/msg/${m.id}`, body, auth());
    await loadMessages();
    await loadThreads();
  };

  return (
    <div className="rmq">
      <header className="rmq-head">
        <span className="rmq-eyebrow">Safety</span>
        <h1>Teacher questions</h1>
        <p>Every conversation between students and their teachers. Reported messages come first.</p>
      </header>
      <p className="rmq-safety">
        <FontAwesomeIcon icon={faShieldHalved} />
        <span>Phone numbers, emails and social media are blocked automatically. Hide a message to remove it for the student and the teacher.</span>
      </p>
      {error && (
        <p className="rmq-error">
          <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
        </p>
      )}
      {!threads ? (
        <div className="rmq-skel" aria-busy="true" />
      ) : (
        <div className="rmq-split">
          <ul className="rmq-list" aria-label="Conversations">
            {threads.length === 0 && <li className="rmq-pick">No conversations yet.</li>}
            {threads.map((t) => (
              <li key={`${t.studentId}-${t.teacherId}`}>
                <button
                  type="button"
                  className={t.studentId === sid && t.teacherId === tid ? 'is-on' : ''}
                  onClick={() => setParams({ student: t.studentId, teacher: t.teacherId }, { replace: true })}
                  style={{ gridTemplateColumns: 'minmax(0, 1fr) auto' }}
                >
                  <span>
                    <b>
                      {t.student} ↔ {t.teacher}
                    </b>
                    <small>
                      {t.count} {t.count === 1 ? 'message' : 'messages'} · {dayOf(t.lastAt)}
                      {t.waiting ? ` · ${t.waiting} not answered` : ''}
                    </small>
                  </span>
                  {t.reported > 0 && <span className="rmq-badge is-red">{t.reported}</span>}
                </button>
              </li>
            ))}
          </ul>
          <section className="rmq-card">
            {!sid || !tid ? (
              <div className="rmq-pick">Choose a conversation to read it.</div>
            ) : !messages ? (
              <div className="rmq-skel" aria-busy="true" />
            ) : (
              <>
                <div className="rmq-card-head">
                  <div>
                    <b>{current ? `${current.student} ↔ ${current.teacher}` : 'Conversation'}</b>
                    <small>Read only · you can hide a message</small>
                  </div>
                </div>
                <Conversation
                  me="admin"
                  names={{ student: current?.student || 'Student', teacher: current?.teacher || 'Teacher' }}
                  messages={messages}
                  onHide={(m) => update(m, { hidden: !m.hidden })}
                  onClearReport={(m) => update(m, { clearReport: true })}
                />
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default TeacherQuestions;
